import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Pause, Play, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Card, Notice, fmt } from "./kit";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";

const Shell = ({ open, onClose, title, desc, testId, children, footer }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="border-white/10 bg-[#0F0E13] sm:max-w-lg" data-testid={testId}>
      <DialogHeader><DialogTitle>{title}</DialogTitle>{desc && <DialogDescription>{desc}</DialogDescription>}</DialogHeader>
      <div className="space-y-4">{children}</div>
      <DialogFooter>{footer}</DialogFooter>
    </DialogContent>
  </Dialog>
);

export function AiDraftDialog({ open, onClose, onApply }) {
  const { lang } = useT();
  const [f, setF] = useState({ brief: "", tone: "friendly", lang });
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/app/campaigns/ai-draft", f); onApply(data); toast.success("Draft ready. Review the text and replace any [placeholders]."); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell open={open} onClose={onClose} title="Write a first draft with AI" desc="Describe what the email is about. The AI writes the subject, an alternative subject, preview text, HTML and plain text. It replaces your current content." testId="ai-draft-dialog"
      footer={<Button onClick={run} disabled={busy || f.brief.trim().length < 10} data-testid="ai-draft-submit"><Sparkles className="mr-1 h-4 w-4" />{busy ? "Writing… (about 20 s)" : "Write draft"}</Button>}>
      <Textarea rows={6} value={f.brief} onChange={(e) => setF({ ...f, brief: e.target.value })} placeholder="e.g. Spring collection is live: 3 new linen shirts, free shipping until Sunday, link to shop" data-testid="ai-draft-brief" />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Tone</Label><Select value={f.tone} onValueChange={(v) => setF({ ...f, tone: v })}><SelectTrigger data-testid="ai-draft-tone"><SelectValue /></SelectTrigger>
          <SelectContent>{["friendly", "professional", "playful", "luxury"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1.5"><Label>Language</Label><Select value={f.lang} onValueChange={(v) => setF({ ...f, lang: v })}><SelectTrigger data-testid="ai-draft-lang"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="fi">Suomi</SelectItem></SelectContent></Select></div>
      </div>
    </Shell>
  );
}

export function TestDialog({ open, onClose, id }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const list = to.split(/[,\s]+/).filter(Boolean);
      const { data } = await api.post(`/app/campaigns/${id}/test`, { to: list });
      toast.success(`Test sent (${data.message_ids.length} message${data.message_ids.length > 1 ? "s" : ""}). Check Messages for its status.`); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell open={open} onClose={onClose} title="Send a test" desc="A real email marked [Test], personalised with the first contact's details. Split tests send both subjects. Leave empty to send to yourself." testId="test-dialog"
      footer={<Button onClick={send} disabled={busy} data-testid="test-send-submit">{busy ? "Sending…" : "Send test"}</Button>}>
      <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com, colleague@example.com (max 3)" data-testid="test-send-to" />
    </Shell>
  );
}

const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

export function SendDialog({ open, onClose, c, audiences, onDone }) {
  const [mode, setMode] = useState("now");
  const [at, setAt] = useState(() => toLocalInput(new Date(Date.now() + 3600_000)));
  const [busy, setBusy] = useState(false);
  const aud = audiences.find((a) => a.id === c.audience_id);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/app/campaigns/${c.id}/send`, { send_at: mode === "later" ? new Date(at).toISOString() : null });
      toast.success(mode === "later" ? "Campaign scheduled" : "Campaign is sending"); onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell open={open} onClose={onClose} title="Send or schedule" testId="send-dialog"
      footer={<Button onClick={go} disabled={busy} data-testid="send-confirm-button">{busy ? "Working…" : mode === "later" ? "Schedule" : `Send to ${aud?.contacts ?? 0} contacts`}</Button>}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        {[["From", c.from], ["Audience", aud ? `${aud.name} (${aud.contacts})` : "—"], ["Subject", c.subject], c.split_test?.enabled && ["Split test", `${c.split_test.test_percent}% test group, winner after ${c.split_test.wait_hours} h`]].filter(Boolean).map(([k, v]) => (
          <div key={k} className="contents"><dt className="text-zinc-500">{k}</dt><dd className="break-all text-zinc-200">{v || "—"}</dd></div>))}
      </dl>
      <div className="flex gap-2">{[["now", "Send now"], ["later", "Schedule"]].map(([k, label]) => (
        <Button key={k} type="button" variant={mode === k ? "default" : "outline"} onClick={() => setMode(k)} className={mode === k ? "" : "border-white/10 bg-transparent"} data-testid={`send-mode-${k}`}>{label}</Button>))}</div>
      {mode === "later" && <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} data-testid="send-at-input" />}
      <Notice tone="info">Suppressed and unsubscribed addresses are skipped automatically. Each email counts toward your plan's limits.</Notice>
    </Shell>
  );
}

const pct = (a, b) => (b ? `${Math.round((a / b) * 1000) / 10}%` : "—");

function Stat({ label, value, sub, testId }) {
  return <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4"><div className="text-xs text-zinc-500">{label}</div>
    <div className="mt-1 font-display text-2xl text-white" data-testid={testId}>{value}</div>{sub && <div className="text-xs text-zinc-500">{sub}</div>}</div>;
}

export function CampaignReport({ c, reload }) {
  const nav = useNavigate();
  const s = c.stats || {};
  const done = (c.progress?.sent || 0) + (c.progress?.skipped || 0);
  const live = ["sending", "scheduled", "testing"].includes(c.status);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
  }, [live, reload]);
  const act = async (path, msg) => {
    try { const { data } = await api.post(`/app/campaigns/${c.id}/${path}`); toast.success(msg); if (path === "duplicate") nav(`/app/campaigns/${data.id}`); else reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delivered = (s.by_status?.delivered || 0) + (s.by_status?.accepted || 0);
  return (
    <div className="space-y-6" data-testid="campaign-report">
      <div className="flex flex-wrap gap-2">
        {live && <Button variant="outline" onClick={() => window.confirm(c.status === "scheduled" ? "Unschedule and return to draft?" : "Stop sending? Emails already sent stay sent.") && act("cancel", "Campaign stopped")} className="border-white/10 bg-transparent" data-testid="campaign-cancel-button"><XCircle className="mr-1 h-4 w-4" />{c.status === "scheduled" ? "Unschedule" : "Stop"}</Button>}
        {c.status === "paused" && <Button onClick={() => act("resume", "Campaign resumed")} data-testid="campaign-resume-button"><Play className="mr-1 h-4 w-4" />Resume</Button>}
        <Button variant="outline" onClick={() => act("duplicate", "Copy created")} className="border-white/10 bg-transparent" data-testid="campaign-duplicate-button"><Copy className="mr-1 h-4 w-4" />Duplicate</Button>
      </div>
      {c.status === "scheduled" && <Notice title={`Scheduled for ${fmt(c.scheduled_at)}`} testId="campaign-scheduled-note">Unschedule to edit it.</Notice>}
      {c.status === "testing" && <Notice title="Split test running" testId="campaign-testing-note">The winner is picked at {fmt(c.test_ends_at)} and then sent to the rest of the audience.</Notice>}
      {c.status === "failed" && <Notice tone="error" title="Nothing was sent" testId="campaign-failed-note">{c.pause_reason}</Notice>}
      {c.status === "paused" && <Notice tone="warn" title="Paused" testId="campaign-paused-note"><Pause className="mr-1 inline h-3.5 w-3.5" />{c.pause_reason}</Notice>}
      <Card testId="campaign-progress">
        <div className="mb-2 flex justify-between text-sm"><span className="text-zinc-300">Progress</span><span className="text-zinc-400" data-testid="campaign-progress-text">{done.toLocaleString()} / {(c.audience_size || 0).toLocaleString()}</span></div>
        <Progress value={c.audience_size ? (done / c.audience_size) * 100 : 0} />
        <p className="mt-2 text-xs text-zinc-500">{c.progress?.skipped || 0} skipped (suppressed, unsubscribed or invalid)</p>
      </Card>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Stat label="Sent" value={(s.messages || 0).toLocaleString()} testId="stat-sent" />
        <Stat label="Accepted / delivered" value={delivered.toLocaleString()} testId="stat-delivered" />
        <Stat label="Opened" value={c.track_opens || c.split_test?.enabled ? pct(s.opened, s.messages) : "Off"} sub={c.track_opens || c.split_test?.enabled ? `${s.opened || 0} people` : null} testId="stat-opened" />
        <Stat label="Clicked" value={c.track_clicks ? pct(s.clicked, s.messages) : "Off"} sub={c.track_clicks ? `${s.clicked || 0} people` : null} testId="stat-clicked" />
        <Stat label="Bounced" value={pct((s.by_status?.bounced || 0) + (s.by_status?.failed || 0), s.messages)} testId="stat-bounced" />
        <Stat label="Unsubscribed" value={(s.unsubscribed || 0).toLocaleString()} testId="stat-unsubscribed" />
      </div>
      {c.split_test?.enabled && (
        <Card testId="campaign-variants">
          <div className="eyebrow mb-4">Split test</div>
          <div className="grid gap-3 sm:grid-cols-2">{["A", "B"].map((v) => {
            const d = s.variants?.[v] || {};
            return <div key={v} className={`rounded-xl border p-4 ${c.winner === v ? "border-emerald-400/40 bg-emerald-500/10" : "border-white/10"}`} data-testid={`variant-${v}`}>
              <div className="text-xs text-zinc-500">Subject {v} {c.winner === v && <span className="text-emerald-300">· winner</span>}</div>
              <div className="mt-1 text-sm text-white">{v === "A" ? c.subject : c.subject_b}</div>
              <div className="mt-2 text-xs text-zinc-400">{d.messages || 0} sent · {pct(d.opened || 0, d.messages)} opened</div></div>;
          })}</div>
        </Card>)}
      <p className="text-xs text-zinc-500">Opens are approximate: some mail apps load images automatically, others block them.</p>
    </div>
  );
}
